<template>
  <bf-modal :escapable="escapable"
            :persistent="persistent"
            :show="visible"
            :size="size"
            :z-index="200"
            placement="center"
            @close="handleCancel">
    <template #body>
      <div class="flex flex-col items-center justify-center gap-5 overflow-y-scroll text-center">
        <bf-badge v-if="avatar" circular color="ghost">
          <div class="relative">
            <bf-avatar :icon="avatar" size="2xl"/>
            <div class="absolute -bottom-3.5 -right-1.5 rounded-full h-10 w-10  flex items-center justify-center">
              <bf-badge :color="iconColor" circular size="lg">
                <font-awesome-icon :icon="computedIcon ?? defaultIcon" class="text-xl"/>
              </bf-badge>
            </div>
          </div>
        </bf-badge>
        <bf-badge v-elses
                  :color="iconColor"
                  size="xl"
                  square>
          <icon-bounding-box :bounding-box="30" :icon="computedIcon ?? defaultIcon"/>
        </bf-badge>
        <p v-if="title" class="font-semibold">{{ title }}</p>
        <div v-if="badges" class="py-2.5 px-3 inline-flex flex-col gap-1">
          <bf-badge v-for="(badge, index) in badges"
                    :key="`${badge.text}-${index}`"
                    :color="badge.color"
                    :icon="getIconMethod(badge.icon)"
                    :outlined="badge.outlined"
                    class="whitespace-nowrap self-center"
                    size="xs">
            {{ badge.text }}
          </bf-badge>
        </div>
        <div class="flex gap-5 flex-col" v-html="text"/>
      </div>
    </template>
    <template #footer>
      <div class="flex justify-center gap-5">
        <bf-button v-if="cancel && cancel.visible" :color="cancel.color" @click="handleCancel">
          {{ cancel.text }}
        </bf-button>
        <bf-button v-if="confirm && confirm.visible" :color="confirm.color" @click="handleConfirm">
          {{ confirm.text }}
        </bf-button>
      </div>
    </template>
  </bf-modal>
</template>

<script lang="ts" setup>

import type {ButtonColor} from '~/components/global/utils/buttonUtils';
import type {ModalSize} from '~/components/global/utils/modalUtils';
import type {ToastBadge} from '~/store/toast';
import {type IconPropType, useIconProp} from '~/components/global/utils/iconPropUtil';
import {FontAwesomeIcon} from '@fortawesome/vue-fontawesome';

export interface ConfirmationButton {
  text: string;
  color: ButtonColor;
  visible: boolean;
}

export interface ConfirmationBoxOptions {
  type?: string;
  icon?: IconPropType;
  avatar: string | null | undefined;
  title?: string;
  text?: string;
  size?: ModalSize;
  badges?: ToastBadge[] | null;
  persistent?: boolean;
  escapable?: boolean;
  confirm?: Partial<ConfirmationButton>;
  cancel?: Partial<ConfirmationButton>;
}

const props = withDefaults(defineProps<ConfirmationBoxOptions>(), {
  type: 'warning',
  icon: 'exclamation-triangle',
  title: 'Confirmation',
  size: 'lg',
  text: 'Are you sure you want to proceed?',
  badges: null,
  persistent: false,
  escapable: true,
  confirm: () => ({
    text: 'Confirm',
    color: 'primary',
    visible: true
  }),
  cancel: () => ({
    text: 'Cancel',
    color: 'outline',
    visible: true
  })
});

const emit = defineEmits(['confirm', 'cancel']);
const {getIconMethod, computedIcon} = useIconProp({icon: props.icon});
const defaultIcon: [string, string] = ['far', 'exclamation-triangle'];

const iconColor = computed(() => {
  if (props.type === 'warning')
    return 'yellow';
  if (props.type === 'success')
    return 'green';
  if (props.type === 'error')
    return 'red';
  if (props.type === 'info')
    return 'blue';

  return 'yellow';
});

const visible = ref(false);
let showTimeout: NodeJS.Timeout | null = null;

onMounted(() => {
  showTimeout = setTimeout(() => {
    visible.value = true;
  }, 10);
});

// deregister the event listener when the component is destroyed
onBeforeUnmount(() => {
  if (showTimeout)
    clearTimeout(showTimeout);
});

function handleConfirm() {
  visible.value = false;
  emit('confirm');
}

function handleCancel() {
  visible.value = false;
  emit('cancel');
}
</script>